import { useRef } from "react";
import { PencilIcon } from "@heroicons/react/24/outline";
import { ChangeEvent, FC, useState } from "react";
import { ITextareaProps } from "../../types";
import { ScrollToFormikError } from "../elements/ScrollToFormikError";

export const TextArea: FC<ITextareaProps> = ({
  className,
  name,
  value,
  id,
  label,
  placeholder,
  disabled,
  readOnly,
  passValidation,
  failedValidation,
  editable,
  isRequired,
  rows,
  maxLength,
  showCharCount, 
  onValueChanged,
  onFocusOut,
  error,
  helperText
}) => {
  const [editField, setEditField] = useState<boolean>(false);
  const [textareaValue, setTextareaValue] = useState<string>(value || "");

  const fieldRef = useRef<HTMLTextAreaElement>(null);

  return (
    <>
      {editable && !editField ? (
        <div className="flex flex-col box-border w-full p-0">
          <label className="w-full text-gray-900 dark:text-darkBg-mainText p-0 mb-1 font-medium text-sm">
            {label} 
          </label>
          <p className="w-full flex flex-row items-center justify-between space-x-4 bg-transparent dark:bg-transparent pl-3 p-1 rounded-md text-gray-700 dark:text-gray-300 group border border-gray-200 dark:border-darkBg-border text-xs">
            <span className="w-full truncate">{value}</span>
            <span
              className="invisible group-hover:visible hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md p-3 cursor-pointer transition-colors"
              onClick={() => {setEditField(true)}}
            >
              <PencilIcon className="w-4 h-4 text-primary-main dark:text-primary-400" />
            </span>
          </p>
        </div>
      ) : (
        <div className="flex flex-col box-border w-full p-0 font-medium text-sm space-y-2">
          {(label || showCharCount) && (
            <div className="w-full text-gray-900 dark:text-darkBg-mainText p-0 flex justify-between">
              <label htmlFor={id}>
                {label} {isRequired && <span className="text-red-500">*</span>}
              </label>
              {maxLength && <span className="text-gray-500 dark:text-gray-400"> {textareaValue?.length} / {maxLength}</span>}
            </div>
          )}

          <div className={`flex flex-row items-center justify-center ${disabled ? "bg-gray-100 dark:bg-gray-800" : ""} relative`}>
            <textarea
              ref={fieldRef}
              id={id}
              className={`
                border outline-none rounded-lg py-3 px-4 text-sm w-full transition-colors resize-none
                ${disabled 
                  ? "border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed" 
                  : "bg-white dark:bg-darkBg text-gray-900 dark:text-darkBg-mainText border-gray-200 dark:border-darkBg-border"
                }
                ${failedValidation ? "border-red-500 dark:border-red-400" : ""}
                ${passValidation ? "border-green-500 dark:border-green-400" : ""}
                placeholder-gray-400 dark:placeholder-gray-500
                ${className}
              `}
              value={value}
              disabled={disabled}
              readOnly={readOnly}
              placeholder={placeholder}
              required={isRequired}
              rows={rows}
              maxLength={maxLength}
              name={name}
              onChange={(ev: ChangeEvent<HTMLTextAreaElement>) => {
                onValueChanged(ev);
                setTextareaValue(ev.target.value);
              }}
              onBlur={(ev: ChangeEvent<HTMLTextAreaElement>) => {
                onFocusOut(ev);
                setTextareaValue(ev.target.value);
              }}
            />
          </div>
          {error && <p className="text-red-500 dark:text-red-400 text-xs mt-1">{String(error)}</p>}
          {helperText && <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">{String(helperText)}</p>}
        </div>
      )}
      <ScrollToFormikError
        fieldName={name ?? id ?? ""}
        fieldRef={fieldRef}
      />
    </>
  );
};

export default TextArea;
